import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, API } from '@/lib/api';
import { ChevronLeft, Loader2, Send, Edit2, Save, X, Plus, Tag, Link2, Lock, Check, Upload, RefreshCw, Link as LinkIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function Preview() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [status, setStatus] = useState('publish');
  const [scheduledAt, setScheduledAt] = useState(''); // ISO local string for datetime-local input
  const [publishing, setPublishing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [imageOptions, setImageOptions] = useState([]);
  const [searchingImages, setSearchingImages] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  // WordPress authors & categories (dynamic per selected site)
  const [wpAuthors, setWpAuthors] = useState([]);
  const [wpCategories, setWpCategories] = useState([]);
  const [authorId, setAuthorId] = useState('');
  const [categoryIds, setCategoryIds] = useState([]); // multi-select
  const [loadingWpMeta, setLoadingWpMeta] = useState(false);
  const [suggestingCats, setSuggestingCats] = useState(false);
  const [aiSuggestedIds, setAiSuggestedIds] = useState([]);
  const fileInputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [a, s] = await Promise.all([
          api.get(`/articles/${articleId}`),
          api.get('/wordpress-sites'),
        ]);
        setArticle(a.data);
        setEditTitle(a.data.title);
        setEditContent(a.data.content_html);
        setTags(a.data.tags || []);
        setImageOptions(a.data.image_options || (a.data.image_url ? [a.data.image_url] : []));
        setSites(s.data);
        // Pre-select the media the article was generated for
        const preferred = a.data.media_id || a.data.site_id;
        if (preferred && s.data.some((x) => x.id === preferred)) {
          setSelectedSiteId(preferred);
          const siteObj = s.data.find((x) => x.id === preferred);
          setStatus(siteObj?.default_status || 'publish');
        } else if (s.data.length > 0) {
          setSelectedSiteId(s.data[0].id);
          setStatus(s.data[0].default_status || 'publish');
        }
      } catch (e) {
        toast.error('Impossible de charger l\'article');
      } finally {
        setLoading(false);
      }
    })();
  }, [articleId]);

  // Load WP authors & categories when the selected site changes
  useEffect(() => {
    if (!selectedSiteId) {
      setWpAuthors([]); setWpCategories([]);
      setAuthorId(''); setCategoryIds([]);
      setAiSuggestedIds([]);
      return;
    }
    const site = sites.find((x) => x.id === selectedSiteId);
    let cancelled = false;
    setLoadingWpMeta(true);
    (async () => {
      try {
        const [aRes, cRes] = await Promise.all([
          api.get(`/wordpress-sites/${selectedSiteId}/authors`).catch(() => ({ data: [] })),
          api.get(`/wordpress-sites/${selectedSiteId}/categories`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setWpAuthors(Array.isArray(aRes.data) ? aRes.data : []);
        setWpCategories(Array.isArray(cRes.data) ? cRes.data : []);
        // Apply media defaults
        if (site?.default_author_id) setAuthorId(String(site.default_author_id));
        else setAuthorId('');
        if (site?.default_category_id) setCategoryIds([Number(site.default_category_id)]);
        else setCategoryIds([]);
      } catch (e) {
        if (!cancelled) {
          setWpAuthors([]); setWpCategories([]);
        }
      } finally {
        if (!cancelled) setLoadingWpMeta(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId]);

  // Auto-suggest categories via AI once categories are loaded (non-blocking, one-shot)
  useEffect(() => {
    if (!selectedSiteId || !articleId || wpCategories.length === 0) return;
    if (categoryIds.length > 0) return;
    let cancelled = false;
    setSuggestingCats(true);
    (async () => {
      try {
        const { data } = await api.post(
          `/articles/${articleId}/suggest-categories`,
          { site_id: selectedSiteId }
        );
        if (cancelled) return;
        const ids = ((data && data.category_ids) || []).map(Number).filter(Number.isFinite);
        if (ids.length > 0) {
          setAiSuggestedIds(ids);
          setCategoryIds(ids);
        }
      } catch (e) { /* silent fallback */ }
      finally { if (!cancelled) setSuggestingCats(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wpCategories.length, selectedSiteId, articleId]);

  const recalcCategories = async () => {
    if (!selectedSiteId || wpCategories.length === 0) return;
    setSuggestingCats(true);
    try {
      const { data } = await api.post(
        `/articles/${articleId}/suggest-categories`,
        { site_id: selectedSiteId }
      );
      const ids = (data.category_ids || []).map(Number);
      setAiSuggestedIds(ids);
      setCategoryIds(ids);
      if (ids.length === 0) toast.info('Aucune catégorie évidente — sélection manuelle');
      else toast.success(`${ids.length} catégorie(s) suggérée(s) par IA`);
    } catch (e) {
      toast.error('Échec de la suggestion IA');
    } finally { setSuggestingCats(false); }
  };

  const toggleCategory = (id) => {
    setCategoryIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // Hard cap: max 2 categories
      if (prev.length >= 2) {
        toast.info('2 catégories maximum');
        return prev;
      }
      return [...prev, id];
    });
  };

  const saveEdits = async () => {
    try {
      const { data } = await api.patch(`/articles/${articleId}`, {
        title: editTitle,
        content_html: editContent,
        tags,
      });
      setArticle(data);
      setEditing(false);
      toast.success('Modifications enregistrées');
    } catch (e) {
      toast.error('Échec de la sauvegarde');
    }
  };

  const addTag = async () => {
    const t = newTag.trim();
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setNewTag('');
      return;
    }
    const updated = [...tags, t];
    setTags(updated);
    setNewTag('');
    try {
      await api.patch(`/articles/${articleId}`, { tags: updated });
    } catch (e) { /* silent */ }
  };

  const removeTag = async (idx) => {
    const updated = tags.filter((_, i) => i !== idx);
    setTags(updated);
    try {
      await api.patch(`/articles/${articleId}`, { tags: updated });
    } catch (e) { /* silent */ }
  };

  // ---------------- IMAGE ----------------
  const selectImage = async (url) => {
    if (!url) return;
    setArticle((a) => ({ ...a, image_url: url }));
    if (!imageOptions.includes(url)) {
      setImageOptions((o) => [...o, url]);
    }
    try {
      await api.patch(`/articles/${articleId}`, { image_url: url });
    } catch (e) {
      toast.error('Échec de la sélection');
    }
  };

  const regenerateImages = async () => {
    setSearchingImages(true);
    try {
      const { data } = await api.post(`/articles/${articleId}/search-images`);
      const opts = data.image_options || [];
      setImageOptions(opts);
      if (opts.length > 0) {
        await selectImage(opts[0]);
      }
      toast.success(`${opts.length} images trouvées`);
    } catch (e) {
      toast.error('Recherche échouée');
    } finally {
      setSearchingImages(false);
    }
  };

  const submitUrl = async () => {
    const u = urlInput.trim();
    if (!u) return;
    await selectImage(u);
    setUrlInput('');
    toast.success('Image ajoutée');
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/articles/${articleId}/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageOptions(data.image_options || []);
      setArticle((a) => ({ ...a, image_url: data.image_url }));
      toast.success('Image téléversée');
    } catch (e) {
      toast.error('Échec du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const publish = async () => {
    if (!selectedSiteId) { toast.error('Ajoutez d\'abord un site WordPress'); return; }
    let scheduledIso = null;
    if (status === 'future') {
      if (!scheduledAt) {
        toast.error('Choisissez une date et une heure de publication');
        return;
      }
      const d = new Date(scheduledAt);
      if (isNaN(d.getTime())) {
        toast.error('Date invalide');
        return;
      }
      if (d.getTime() <= Date.now()) {
        toast.error('La date de planification doit être dans le futur');
        return;
      }
      // Send the LOCAL datetime as chosen by the user + the user's timezone offset
      // so the backend can compute the correct UTC date_gmt regardless of WP's tz.
      // Format: ISO with explicit offset, e.g. "2026-04-29T22:55:00+02:00"
      const tzMinutes = -d.getTimezoneOffset();
      const sign = tzMinutes >= 0 ? '+' : '-';
      const absMin = Math.abs(tzMinutes);
      const hh = String(Math.floor(absMin / 60)).padStart(2, '0');
      const mm = String(absMin % 60).padStart(2, '0');
      scheduledIso = `${scheduledAt}:00${sign}${hh}:${mm}`;
    }
    setPublishing(true);
    try {
      // Make sure latest tags are saved before publishing
      await api.patch(`/articles/${articleId}`, { tags });
      const { data } = await api.post(`/articles/${articleId}/publish`, {
        site_id: selectedSiteId,
        status,
        author_id: authorId ? Number(authorId) : null,
        category_ids: categoryIds.length > 0 ? categoryIds : null,
        ...(scheduledIso ? { scheduled_at: scheduledIso } : {}),
      });
      sessionStorage.setItem('autoradko_last_publish', JSON.stringify(data));
      nav(`/success/${articleId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la publication');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-shell flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="mobile-shell flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <p className="text-slate-700 font-semibold mb-2">Article introuvable</p>
        <button
          onClick={() => nav('/')}
          className="mt-4 bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl"
          data-testid="btn-back-feed-from-preview"
        >
          Retour au feed
        </button>
      </div>
    );
  }

  // Resolve image URL (supports our /api/uploads paths)
  const resolveImg = (u) => {
    if (!u || typeof u !== 'string') return '';
    return u.startsWith('/api/') ? `${API.replace('/api', '')}${u}` : u;
  };

  return (
    <div className="mobile-shell">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav('/')} data-testid="btn-back" className="flex items-center gap-1 text-slate-700 font-medium">
          <ChevronLeft size={22} /> Retour
        </button>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="text-blue-600 font-semibold text-sm flex items-center gap-1"
            data-testid="btn-edit"
          >
            <Edit2 size={14} /> Modifier
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(false); setEditTitle(article.title); setEditContent(article.content_html); }}
              className="text-slate-500 text-sm flex items-center gap-1" data-testid="btn-cancel-edit">
              <X size={14} /> Annuler
            </button>
            <button onClick={saveEdits} className="text-blue-600 font-semibold text-sm flex items-center gap-1" data-testid="btn-save-edit">
              <Save size={14} /> Enregistrer
            </button>
          </div>
        )}
      </header>

      <div className="px-5 pt-5 pb-40">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Aperçu de l'article</h2>

        {/* ============ FACT-CHECK BADGE (Batch A) ============ */}
        {article.fact_check && article.fact_check.score_risque !== null && article.fact_check.score_risque !== undefined && (
          <div
            className={`mb-5 rounded-2xl border p-4 ${
              article.fact_check.score_risque >= 70
                ? 'bg-red-50 border-red-200'
                : article.fact_check.score_risque >= 40
                ? 'bg-amber-50 border-amber-200'
                : article.fact_check.score_risque >= 20
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-emerald-50 border-emerald-200'
            }`}
            data-testid="fact-check-badge"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900">Vérification factuelle</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    article.fact_check.score_risque >= 70
                      ? 'bg-red-600 text-white'
                      : article.fact_check.score_risque >= 40
                      ? 'bg-amber-600 text-white'
                      : article.fact_check.score_risque >= 20
                      ? 'bg-yellow-500 text-white'
                      : 'bg-emerald-600 text-white'
                  }`}
                  data-testid="fact-check-score"
                >
                  Risque {article.fact_check.score_risque}/100
                </span>
              </div>
              <span className="text-xs text-slate-600">
                {article.fact_check.score_risque >= 70
                  ? 'Dangereux'
                  : article.fact_check.score_risque >= 40
                  ? 'À revoir'
                  : article.fact_check.score_risque >= 20
                  ? 'Correct'
                  : 'Sûr'}
              </span>
            </div>
            {article.fact_check.notes && (
              <p className="text-xs text-slate-700 mb-2 italic" data-testid="fact-check-notes">
                {article.fact_check.notes}
              </p>
            )}
            {article.fact_check.affirmations_non_sourcees && article.fact_check.affirmations_non_sourcees.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs font-semibold text-slate-800 cursor-pointer">
                  {article.fact_check.affirmations_non_sourcees.length} affirmation{article.fact_check.affirmations_non_sourcees.length > 1 ? 's' : ''} non sourcée{article.fact_check.affirmations_non_sourcees.length > 1 ? 's' : ''}
                </summary>
                <ul className="mt-2 pl-4 space-y-1" data-testid="fact-check-unsourced">
                  {article.fact_check.affirmations_non_sourcees.map((claim, i) => (
                    <li key={i} className="text-xs text-slate-700 list-disc">{claim}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* ============ IMAGE SELECTOR (4 options) ============ */}
        <div className="mb-6" data-testid="image-selector">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900 text-sm">Image de couverture</h3>
            <button
              onClick={regenerateImages}
              disabled={searchingImages}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
              data-testid="btn-regenerate-images"
            >
              {searchingImages ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Relancer la recherche
            </button>
          </div>

          {/* Currently selected — big preview */}
          {article.image_url && (
            <div className="relative mb-3">
              <img
                src={resolveImg(article.image_url)}
                alt={article.title || ''}
                className="w-full rounded-2xl aspect-video object-cover bg-slate-100"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid="selected-image"
              />
              <div className="absolute top-2 left-2 px-2.5 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full flex items-center gap-1 shadow-md">
                <Check size={12} /> Sélectionnée
              </div>
            </div>
          )}

          {/* Options grid: images 1/2/3 */}
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="image-options">
            {[0, 1, 2].map((i) => {
              const url = imageOptions[i];
              const isSelected = url && article.image_url === url;
              return (
                <button
                  key={i}
                  onClick={() => url && selectImage(url)}
                  disabled={!url}
                  data-testid={`img-option-${i}`}
                  className={`relative aspect-video rounded-xl overflow-hidden border-2 transition active:scale-95 ${isSelected ? 'border-blue-600 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-400'} ${!url ? 'bg-slate-100 opacity-60' : 'bg-slate-50'}`}
                >
                  {url ? (
                    <>
                      <img
                        src={resolveImg(url)}
                        alt={`Image ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.innerHTML =
                              '<div class="w-full h-full flex items-center justify-center text-xs text-slate-400">Indisponible</div>';
                          }
                        }}
                      />
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow">
                          <Check size={14} />
                        </div>
                      )}
                      <div className="absolute bottom-1 left-1 text-[10px] font-bold bg-white/90 text-slate-700 px-1.5 py-0.5 rounded">
                        {i + 1}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                      Image {i + 1}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Option 4: upload / URL */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="image-option-4">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Option 4 — Votre image
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-600 hover:text-blue-700 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition disabled:opacity-60"
                data-testid="btn-upload-image"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Téléverser
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => onUploadFile(e.target.files?.[0])}
                className="hidden"
                data-testid="input-upload-image"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3">
                <LinkIcon size={13} className="text-slate-400 flex-shrink-0" />
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="Coller un lien image…"
                  className="flex-1 py-2 text-sm outline-none"
                  data-testid="input-image-url"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitUrl(); } }}
                />
              </div>
              <button
                onClick={submitUrl}
                disabled={!urlInput.trim()}
                className="px-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 active:scale-95 disabled:opacity-50 text-sm"
                data-testid="btn-submit-image-url"
              >
                OK
              </button>
            </div>
            <p className="text-[11px] text-slate-500">
              Toutes les images sont automatiquement converties en JPEG avant publication.
            </p>
          </div>
        </div>

        {!editing ? (
          <>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight" data-testid="preview-title">
              {article.title}
            </h1>
            <div
              className="prose-article mt-4"
              data-testid="preview-content"
              dangerouslySetInnerHTML={{ __html: article.content_html }}
            />
          </>
        ) : (
          <>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full text-xl font-bold px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
              data-testid="edit-title-input"
            />
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={15}
              className="w-full mt-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none text-sm font-mono"
              data-testid="edit-content-input"
            />
            <p className="text-xs text-slate-500 mt-1">HTML supporté (balises &lt;p&gt;, &lt;h3&gt;, &lt;ul&gt;…)</p>
          </>
        )}

        <div className="mt-8 space-y-3">
          {/* ============== TAGS / Étiquettes WordPress ============== */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="block-tags">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={16} className="text-blue-600" />
              <h3 className="font-bold text-slate-900 text-sm">Étiquettes WordPress</h3>
              <span className="text-xs text-slate-400 ml-auto">{tags.length} tag{tags.length > 1 ? 's' : ''}</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="tags-list">
              {tags.map((t, i) => (
                <span
                  key={`${t}-${i}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium"
                  data-testid={`tag-${i}`}
                >
                  {t}
                  <button
                    onClick={() => removeTag(i)}
                    className="hover:bg-blue-200 rounded-full p-0.5 ml-0.5"
                    data-testid={`btn-remove-tag-${i}`}
                    aria-label={`Supprimer ${t}`}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
              {tags.length === 0 && (
                <span className="text-xs text-slate-400 italic">Aucune étiquette</span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Ajouter une étiquette…"
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none"
                data-testid="input-new-tag"
              />
              <button
                onClick={addTag}
                disabled={!newTag.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-95 transition disabled:opacity-50"
                data-testid="btn-add-tag"
                aria-label="Ajouter"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* ============== SOURCES (INTERNE) ============== */}
          {article.sources && article.sources.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="block-sources-internal">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={14} className="text-amber-700" />
                <h3 className="font-bold text-amber-900 text-sm">Sources utilisées</h3>
                <span className="text-[10px] uppercase tracking-wide font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md ml-auto">
                  Interne — non publié
                </span>
              </div>
              <p className="text-xs text-amber-800 mb-3">
                Visibles uniquement ici pour traçabilité. Jamais envoyées sur WordPress.
              </p>
              <ul className="space-y-1.5">
                {article.sources.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`source-${i}`}>
                    <Link2 size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer"
                        className="text-amber-900 hover:underline break-all">
                        {s.name}
                      </a>
                    ) : (
                      <span className="text-amber-900">{s.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Site WordPress</label>
            <select
              value={selectedSiteId}
              onChange={(e) => {
                setSelectedSiteId(e.target.value);
                const s = sites.find(x => x.id === e.target.value);
                if (s) setStatus(s.default_status || 'publish');
              }}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
              data-testid="select-site"
            >
              {sites.length === 0 && <option value="">Aucun site configuré</option>}
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {sites.length === 0 && (
              <button onClick={() => nav('/settings/sites')}
                className="mt-2 text-sm text-blue-600 font-semibold" data-testid="btn-go-sites">
                + Ajouter un site WordPress
              </button>
            )}
          </div>

          {/* ============== AUTEUR (dynamique depuis WP) ============== */}
          {selectedSiteId && (
            <div data-testid="block-author">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Auteur</label>
                {loadingWpMeta && <Loader2 size={12} className="animate-spin text-slate-400" />}
              </div>
              {wpAuthors.length > 0 ? (
                <select
                  value={authorId}
                  onChange={(e) => setAuthorId(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
                  data-testid="select-author"
                >
                  <option value="">— Auteur par défaut du site —</option>
                  {wpAuthors.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              ) : (
                <div className="text-xs text-slate-500 italic px-2 py-3 bg-slate-50 rounded-xl border border-slate-200">
                  {loadingWpMeta ? 'Chargement…' : 'Aucun auteur récupéré. Vérifiez les identifiants WordPress du média.'}
                </div>
              )}
            </div>
          )}

          {/* ============== CATÉGORIES (multi-sélection dynamique) ============== */}
          {selectedSiteId && (
            <div data-testid="block-categories">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Catégories <span className="text-slate-400 normal-case">(1-2 max)</span>
                  {categoryIds.length > 0 && <span className="text-blue-600 ml-1">· {categoryIds.length}/2</span>}
                </label>
                <div className="flex items-center gap-2">
                  {(loadingWpMeta || suggestingCats) && <Loader2 size={12} className="animate-spin text-slate-400" />}
                  {wpCategories.length > 0 && (
                    <button
                      type="button"
                      onClick={recalcCategories}
                      disabled={suggestingCats}
                      data-testid="btn-suggest-categories"
                      className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
                    >
                      <Sparkles size={12} /> Suggérer par IA
                    </button>
                  )}
                </div>
              </div>

              {/* Selected chips — visible directly without scrolling */}
              {wpCategories.length > 0 && categoryIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="selected-categories-chips">
                  {categoryIds.map((cid) => {
                    const c = wpCategories.find((x) => x.id === cid);
                    if (!c) return null;
                    const suggested = aiSuggestedIds.includes(cid);
                    return (
                      <span
                        key={cid}
                        data-testid={`chip-category-${cid}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${suggested ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}
                      >
                        {suggested && <Sparkles size={11} />}
                        {c.name}
                        <button
                          type="button"
                          onClick={() => toggleCategory(cid)}
                          data-testid={`chip-remove-${cid}`}
                          className="hover:bg-white/60 rounded-full p-0.5 ml-0.5"
                          aria-label={`Retirer ${c.name}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {wpCategories.length > 0 ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-2 max-h-48 overflow-y-auto space-y-1" data-testid="categories-list">
                  {wpCategories.map((c) => {
                    const checked = categoryIds.includes(c.id);
                    const suggested = aiSuggestedIds.includes(c.id);
                    const disabled = !checked && categoryIds.length >= 2;
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition ${checked ? 'bg-blue-50 cursor-pointer' : disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-white'}`}
                        data-testid={`category-option-${c.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleCategory(c.id)}
                          className="w-4 h-4 accent-blue-600 flex-shrink-0"
                        />
                        <span className={`text-sm flex-1 ${checked ? 'font-semibold text-blue-700' : 'text-slate-700'}`}>
                          {c.name}
                        </span>
                        {suggested && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <Sparkles size={9} /> IA
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-slate-500 italic px-2 py-3 bg-slate-50 rounded-xl border border-slate-200">
                  {loadingWpMeta ? 'Chargement…' : 'Aucune catégorie récupérée.'}
                </div>
              )}
              {wpCategories.length > 0 && categoryIds.length === 0 && (
                <p className="text-[11px] text-amber-600 mt-1.5">
                  ⚠︎ Sélectionnez au moins 1 catégorie (sinon WordPress utilisera "Non classé").
                </p>
              )}
              {aiSuggestedIds.length > 0 && categoryIds.length > 0 && (
                <p className="text-[11px] text-purple-600 mt-1.5 flex items-center gap-1">
                  <Sparkles size={10} /> Pré-sélection par l'IA — ajustez librement.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              Statut WordPress
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setStatus('draft')}
                className={`py-3 rounded-xl font-bold border-2 transition flex flex-col items-center gap-0.5 ${status === 'draft' ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="btn-status-draft"
              >
                <span className="text-sm">📝 Brouillon</span>
                <span className="text-[9px] font-medium opacity-70 leading-tight px-1">Relire avant</span>
              </button>
              <button
                onClick={() => setStatus('publish')}
                className={`py-3 rounded-xl font-bold border-2 transition flex flex-col items-center gap-0.5 ${status === 'publish' ? 'bg-green-50 border-green-600 text-green-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="btn-status-publish"
              >
                <span className="text-sm">🚀 Publier</span>
                <span className="text-[9px] font-medium opacity-70 leading-tight px-1">Immédiat</span>
              </button>
              <button
                onClick={() => setStatus('future')}
                className={`py-3 rounded-xl font-bold border-2 transition flex flex-col items-center gap-0.5 ${status === 'future' ? 'bg-purple-50 border-purple-600 text-purple-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="btn-status-future"
              >
                <span className="text-sm">📅 Planifier</span>
                <span className="text-[9px] font-medium opacity-70 leading-tight px-1">Date/heure</span>
              </button>
            </div>

            {status === 'future' && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-xl" data-testid="schedule-block">
                <label className="block text-[11px] font-bold uppercase tracking-wide text-purple-700 mb-1.5">
                  📅 Date et heure de publication
                </label>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  className="w-full px-3 py-2.5 text-base font-semibold bg-white border-2 border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  data-testid="input-scheduled-at"
                />
                <p className="text-[10px] text-purple-700 mt-1.5">
                  L'article sera publié automatiquement par WordPress à l'heure exacte choisie (à la minute près).
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="shell-dock bg-white border-t border-slate-200 p-4">
        <button
          onClick={publish}
          disabled={publishing || !selectedSiteId || editing || (status === 'future' && !scheduledAt)}
          data-testid="btn-publish-wp"
          className={`w-full text-white font-semibold py-3.5 rounded-xl active:scale-95 transition flex items-center justify-center gap-2 disabled:opacity-60 ${
            status === 'draft' ? 'bg-amber-600 hover:bg-amber-700'
            : status === 'future' ? 'bg-purple-600 hover:bg-purple-700'
            : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {publishing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {publishing
            ? (status === 'draft' ? 'Envoi en brouillon…' : status === 'future' ? 'Planification…' : 'Publication…')
            : (status === 'draft' ? 'Envoyer en brouillon sur WordPress'
              : status === 'future' ? (scheduledAt ? 'Planifier la publication' : 'Choisir une date/heure d\'abord')
              : 'Publier sur WordPress')}
        </button>
      </div>
    </div>
  );
}
